import { useMemo } from 'react'
import { Alert, Card, Steps, Typography } from 'antd'
import { getProductLabel } from './utils'
import { SignupProvider, useSignup, type FlowStep } from './SignupContext'
import AuthLogo from '../components/AuthLogo'
import AuthHighlights from '../components/AuthHighlights'
import AuthFooterLinks from '../components/AuthFooterLinks'
import ProductStepForm from './components/ProductStepForm'
import ClientInfoStepForm from './components/ClientInfoStepForm'
import PricingStepForm from './components/PricingStepForm'
import LoginStepForm from './components/LoginStepForm'
import PaymentStepWrapper from './components/PaymentStepWrapper'
import ImportDataStep from './components/ImportDataStep'
import ExistingUserModal from './components/ExistingUserModal'
import BasExistsModal from './components/BasExistsModal'
import AdvicePrepProductStep from './adviceprep/AdvicePrepProductStep'
import AdvicePrepPricingStep from './adviceprep/AdvicePrepPricingStep'
import AdvicePrepSuccessStep from './adviceprep/AdvicePrepSuccessStep'
import AdvicePrepHighlights from './adviceprep/AdvicePrepHighlights'
import AdvicePrepConfirmModal from './adviceprep/AdvicePrepConfirmModal'
import { useSignupFlow } from './useSignupFlow'
import { normalizeCustomerForm } from './utils'
import './Signup.css'

const { Text, Title } = Typography

const STEP_LABELS: Record<FlowStep, string> = {
  product: 'Platform',
  info: 'Client Info',
  pricing: 'Pricing',
  payment: 'Payment',
  login: 'Login',
  importData: 'Data',
  success: 'Done',
}

function SignupContent() {
  const { step, visibleSteps, productSelection, isBasSignup, isAdvicePrepSignup, error, info } = useSignup()
  const productLabel = getProductLabel(productSelection?.product, isBasSignup, isAdvicePrepSignup)
  const stripePk = (import.meta.env['VITE_STRIPE_PK'] as string | undefined) || ''
  const steps = useMemo(() => visibleSteps.map((flowStep) => STEP_LABELS[flowStep]), [visibleSteps])
  const stepIndex = visibleSteps.indexOf(step)

  const stepContent: Record<FlowStep, React.ReactNode> = useMemo(
    () => ({
      product: isAdvicePrepSignup ? <AdvicePrepProductStep /> : <ProductStepForm />,
      info: <ClientInfoStepForm />,
      pricing: isAdvicePrepSignup ? <AdvicePrepPricingStep /> : <PricingStepForm />,
      login: <LoginStepForm />,
      payment: <PaymentStepWrapper stripePk={stripePk} />,
      importData: <ImportDataStep />,
      success: <AdvicePrepSuccessStep />,
    }),
    [isAdvicePrepSignup, stripePk]
  )

  return (
    <Card className="rounded-[1rem] border border-[#bde2e1] bg-white p-[0.25rem] shadow-none">
      <Title level={3} className="!mb-[0.25rem] !text-[1.75rem] !font-medium !leading-[1.2] !text-gray-900">
        {step === 'importData' ? `${productLabel} Connection` : `${productLabel} Sign Up`}
      </Title>
      <Text type="secondary" className="text-[0.875rem]">
        Please enter your details
      </Text>

      <div className="mt-[1rem]">
        <Steps size="small" current={stepIndex} items={steps.map((label) => ({ title: label }))} />
      </div>

      <div className="mt-[1.25rem]">
        {error && <Alert className="mb-4" type="error" showIcon message={error} />}
        {info && <Alert className="mb-4" type="info" showIcon message={info} />}
        {stepContent[step]}
      </div>

      <AuthFooterLinks showSignIn compact />
    </Card>
  )
}

function SignupModalsAndContent() {
  const {
    runAction,
    customerForm,
    customerInfoForm,
    setCustomerForm,
    setIsChildClient,
    setStep,
    setShowExistingUserModal,
    showExistingUserModal,
    showBasExistsModal,
    setShowBasExistsModal,
    existingCompanyUser,
    loading,
    nocc,
  } = useSignup()

  const handleExistingUserConfirm = async () => {
    setShowExistingUserModal(false)
    await runAction(async () => {
      const values = customerForm ?? normalizeCustomerForm(await customerInfoForm.validateFields(), true)
      setCustomerForm(normalizeCustomerForm(values, true))
      setIsChildClient(true)
      if (nocc) setStep('login')
      else setStep('pricing')
    }, false)
  }

  return (
    <>
      <SignupContent />
      <AdvicePrepConfirmModal />
      <ExistingUserModal
        open={showExistingUserModal}
        loading={loading}
        onOk={handleExistingUserConfirm}
        onCancel={() => setShowExistingUserModal(false)}
      />
      <BasExistsModal
        open={showBasExistsModal}
        existingCompanyUser={existingCompanyUser}
        onClose={() => setShowBasExistsModal(false)}
      />
    </>
  )
}

export default function SignupPage() {
  const contextValue = useSignupFlow()

  return (
    <div className="signup-page min-h-screen bg-[#d1e8e8]">
      <div className="px-[1.5rem] pt-[1.5rem] sm:px-[2rem]">
        <AuthLogo className="h-[3rem] w-auto" />
      </div>

      <main className="px-[1.5rem] pb-[2.5rem] pt-[2.25rem] sm:px-[2rem] lg:px-[2.5rem]">
        <div className="mx-auto grid w-full max-w-[82rem] grid-cols-1 items-start gap-[2.5rem] lg:grid-cols-[1fr_34rem] lg:gap-[4rem]">
          <section className="max-w-[33rem] lg:pt-[1.5rem]">
            <h1 className="font-display text-[2.25rem] font-bold leading-[1.08] text-gray-900">Bring your business to life</h1>
            <p className="mt-[0.25rem] text-[1.625rem] leading-[1.1] text-gray-800">a new way of working</p>
            {contextValue.isAdvicePrepSignup ? <AdvicePrepHighlights /> : <AuthHighlights compact />}
          </section>

          <section className="w-full">
            <SignupProvider value={contextValue}>
              <SignupModalsAndContent />
            </SignupProvider>
          </section>
        </div>
      </main>
    </div>
  )
}
